import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../../stores/useAuthStore';
import { getDefaultRouteForRole, UserRole } from '../../../constants/roles';
import logo from '../../../assets/Header/magnetix.png';
import './Login.css';
import { useRef, useEffect } from 'react';

// Import the eye icons
import { FiEye, FiEyeOff } from 'react-icons/fi';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [showEmail, setShowEmail] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const handleSubmit = async () => {
    console.log('Login submitted', { email, password, rememberMe });

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    try {
      setError('');
      await login(email, password);

      // Get updated user from store
      const { user } = useAuthStore.getState();
      if (user) {
        const defaultRoute = getDefaultRouteForRole(user.role as UserRole);
        navigate(defaultRoute);
      } else {
        navigate('/workspace');
      }
    } catch (err: any) {
      // Provide specific error messages based on error type
      if (err.status === 401) {
        setError('Invalid email or password');
      } else if (err.type === 'network') {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
      console.error('Login error:', err);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

      useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setShowEmail(false);
        }
      }

      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

  return (
    <div className="login-page-container">
      <div className="login-page-header">
        <div className="login-page-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img src={logo} alt="Magnetix Logo" className="login-page-logo-image" />
        </div>
      </div>

      <div className="login-page-card">
        <h1 className="login-page-title">Sign in to Mkraft LMS</h1>
        <p className="login-page-subtitle">Access your courses, track progress, and <span className="login-page-subtitle-highlight">achieve your learning goals</span></p>

        <div className="login-page-form">
          {/* Session Message (from auth events) */}
          {(location.state as any)?.message && (
            <div style={{
              padding: '12px',
              marginBottom: '16px',
              backgroundColor: '#FEF3C7',
              border: '1px solid #F59E0B',
              borderRadius: '8px',
              color: '#92400E',
              fontSize: '14px'
            }}>
              {(location.state as any).message}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div style={{
              padding: '12px',
              marginBottom: '16px',
              backgroundColor: '#FEE',
              border: '1px solid #EF4444',
              borderRadius: '8px',
              color: '#B91C1C',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <div className="login-page-form-group">
            <label className="login-page-label" htmlFor="email">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              className="login-page-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder=""
            />
          </div>

          <div className="login-page-form-group">
            <label className="login-page-label" htmlFor="password">
              Password
            </label>
            <div className="login-page-password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className="login-page-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder=""
              />
              <button
                type="button"
                className="login-page-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label="Toggle password visibility"
              >
                {/* Use react-icons instead of SVG */}
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>
          </div>

          <div className="login-page-options">
            <label className="login-page-checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="login-page-checkbox"
              />
              <span className="login-page-checkbox-custom"></span>
              Remember Me
            </label>
            <button
              type="button"
              onClick={handleForgotPassword}
              className="login-page-forgot-link"
            >
              Forgot Password?
            </button>
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            className="login-page-submit-button"
          >
            Sign In
          </button>
        </div>

        <div ref={containerRef} className="login-page-footer">
        Need Help?{' '}
        <a
          onClick={() => setShowEmail(true)}
          className="login-page-support-link"
        >
          Connect with Support
        </a>

        {showEmail && (
          <div style={{ marginTop: '8px' }}>
            <a href="mailto:support@multiplierskraft.com">
              support@multiplierskraft.com
            </a>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default Login;
